import React from "react";
import Image from "next/legacy/image";

import { format, intervalToDuration } from "date-fns";
import {
  UpcomingClassContainer,
  UpcomingClassDetailsContainer,
  UpcomingClassCardDetailsHeading,
  UpcomingClassCardDetailsTeacher,
  UpcomingClassCardInfoItemContainer,
  UpcomingClassCardInfoItem,
  UpcomingClassMoreDetailsContainer,
  UpcomingClassCardTopicsContainer,
  UpcomingClassCardImageWrapper,
  UpcomingClassDetailsBlock,
  UpcomingClassCardTimer,
  UpcomingClassCardTimerHighlight,
  UpcomingClassCardTimerDots,
  UpcomingClassButtonWrapper,
  SVGContainer
} from "./upcoming-class-card.styles";
import { GuruJuaraBadge } from "../assets/guru-juara-badge";
import { Button } from "components/atoms/button/button";

import {
  getClassTimeStamp,
  getMediaURL,
  getTimeStampFromDateString
} from "../utils";
import { IUpcomingClassCardProps } from "./upcoming-class-card.types";
import { SubjectTag } from "components/subject-tag/subject-tag";
import { getIndonesianTimezone, trimString } from "utils/utils";
import { SubjectTagData } from "components/subject-tag/subject-tag.data";
import trackEvent from "services/mixpanel/mixpanel";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import { ClassTags } from "../class-tags/class-tags";

const UpcomingClassCard: React.FC<IUpcomingClassCardProps> = ({
  classSession,
  baseMediaURL,
  currentTimeStamp,
  handleShowMoreDetails,
  isWidthApplied = true,
  isHeroSection = false,
  heroSectionMeta,
  cardPosition
}) => {
  const classSessionUpcomingTime = intervalToDuration({
    start: getTimeStampFromDateString(currentTimeStamp),
    end: getTimeStampFromDateString(classSession.startDate)
  });
  const subjectTagData = SubjectTagData[classSession.subject.toUpperCase()];

  const handleClick = () => {
    trackEvent({
      eventName: kelasLiveEvents.upcomingClassDetailClicked,
      payload: {
        liveHomePageDesignVersion:
          "Version 3 - Pinned Card of Concept and Extra Class",
        clickedFrom: isHeroSection
          ? "Pinned Card of concept and extra class"
          : null,
        cardPosition: cardPosition || null
      }
    });
    handleShowMoreDetails();
  };

  return (
    <UpcomingClassContainer
      isHeroSection={isHeroSection}
      heroSectionMeta={heroSectionMeta}
      isWidthApplied={isWidthApplied}
      onClick={handleClick}
      data-testid="upcoming-class-card"
    >
      <SVGContainer />
      <UpcomingClassDetailsContainer isHeroSection={isHeroSection}>
        <UpcomingClassCardImageWrapper>
          <Image
            src={getMediaURL(baseMediaURL, classSession.teacher.avatarUrl)}
            alt={classSession.teacher.name}
            layout="fill"
            objectFit="fill"
          />
        </UpcomingClassCardImageWrapper>
        <UpcomingClassDetailsBlock>
          {!isHeroSection && (
            <UpcomingClassCardTopicsContainer>
              <li>
                <SubjectTag
                  backgroundColor={subjectTagData?.backgroundColor}
                  color={subjectTagData?.color}
                  text={subjectTagData?.text}
                  iconSrc={subjectTagData?.iconSrc}
                />
              </li>
              <ClassTags tags={classSession.tags} trimTag />
            </UpcomingClassCardTopicsContainer>
          )}
          <UpcomingClassCardDetailsHeading>
            {classSession.topic}
          </UpcomingClassCardDetailsHeading>
          <UpcomingClassCardDetailsTeacher>
            {trimString(classSession.teacher.name, 40)} <GuruJuaraBadge />
          </UpcomingClassCardDetailsTeacher>
          <UpcomingClassCardInfoItemContainer>
            <UpcomingClassCardInfoItem>
              {getClassTimeStamp(classSession.startDate)}
            </UpcomingClassCardInfoItem>
            <UpcomingClassCardInfoItem>
              {format(
                getTimeStampFromDateString(classSession.startDate),
                "k:mm"
              )}{" "}
              {getIndonesianTimezone()}
            </UpcomingClassCardInfoItem>
            <UpcomingClassCardInfoItem>
              {classSession.duration} menit
            </UpcomingClassCardInfoItem>
          </UpcomingClassCardInfoItemContainer>
        </UpcomingClassDetailsBlock>
      </UpcomingClassDetailsContainer>
      <UpcomingClassMoreDetailsContainer isHeroSection={isHeroSection}>
        {!isHeroSection && (
          <UpcomingClassCardTimer>
            <span>Mulai dalam </span>
            <UpcomingClassCardTimerHighlight>
              {classSessionUpcomingTime.days} hari
            </UpcomingClassCardTimerHighlight>
            <UpcomingClassCardTimerDots>:</UpcomingClassCardTimerDots>
            <UpcomingClassCardTimerHighlight>
              {classSessionUpcomingTime.hours} jam
            </UpcomingClassCardTimerHighlight>
            <UpcomingClassCardTimerDots>: </UpcomingClassCardTimerDots>
            <UpcomingClassCardTimerHighlight>
              {classSessionUpcomingTime.minutes} menit
            </UpcomingClassCardTimerHighlight>
          </UpcomingClassCardTimer>
        )}
        <UpcomingClassButtonWrapper>
          <Button
            variant="ghost"
            onClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              handleClick();
              handleShowMoreDetails();
            }}
          >
            Cek detail kelas
          </Button>
        </UpcomingClassButtonWrapper>
      </UpcomingClassMoreDetailsContainer>
    </UpcomingClassContainer>
  );
};

export { UpcomingClassCard };
